import { useState, useEffect, useMemo } from "react";
import { Plus, X, ArrowUp, ArrowDown, Pencil, Trash2, HelpCircle, Info, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../primitives/Button";
import { IconButton } from "../../primitives/IconButton";
import { Modal } from "../../primitives/Modal";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { StepConditionsBuilder } from "./StepConditionsBuilder";
import type {
  ProcessStepTemplate,
  ProcessActivityTemplate,
  ProcessTriggerTemplate,
  CompletionPolicy,
  ActivityType,
  TriggerEventType,
  TriggerActionType,
  StepConditions,
  AssigneeActivityConfig,
  ActivityUIConfig,
  ActivityUIField,
  ActivityUIConditionalRule,
  ActivityUIFieldType,
  ActivityUIActionRule,
  ActivityUIButton,
  ActivityUIAction,
  ActivityUIButtonVisibility,
} from "../../../api/processes";
import { listEmailTemplates } from "../../../api/emailSend";
import { listAmbassadorOrgs, listCustomRoles, getAdminOrgId } from "../../../api/admin";

// Activity type options grouped by category
const ACTIVITY_TYPE_OPTIONS = [
  { value: "call_client", label: "Appel client", category: "Communication" },
  { value: "send_email_manual", label: "Envoyer courriel", category: "Communication" },
  { value: "meeting", label: "Réunion", category: "Communication" },
  { value: "client_confirmation", label: "Confirmation client", category: "Communication" },
  { value: "validate_document", label: "Valider document", category: "Administratif" },
  { value: "upload_file", label: "Téléverser fichier", category: "Administratif" },
  { value: "approve_internal", label: "Approuver interne", category: "Administratif" },
  { value: "fill_form", label: "Remplir formulaire", category: "Administratif" },
  { value: "process_data", label: "Traiter données", category: "Opérationnel" },
  { value: "execute_service", label: "Exécuter service", category: "Opérationnel" },
  { value: "quality_check", label: "Contrôle qualité", category: "Opérationnel" },
  { value: "add_operational_cost", label: "Ajouter un coût", category: "Opérationnel" },
  { value: "assign_responsible", label: "Assigner un responsable", category: "Assignation" },
  { value: "schedule_followup", label: "Planifier suivi", category: "Suivi" },
  { value: "create_note", label: "Créer note", category: "Suivi" },
  { value: "system_validation", label: "Validation système", category: "Système" },
];

// Base option for "no template"; full list loaded from API in StepEditorModal
const EMAIL_TEMPLATE_OPTIONS_DEFAULT = [{ value: "", label: "Aucun template" }];
const EMAIL_RECIPIENT_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "assignee", label: "Assigné" },
  { value: "role", label: "Rôle spécifique" },
  { value: "user", label: "Utilisateur précis" },
];

// Trigger action options grouped by category
const TRIGGER_ACTION_OPTIONS = [
  { value: "create_alert", label: "Créer alerte", category: "Alertes" },
  { value: "schedule_reminder", label: "Planifier rappel", category: "Alertes" },
  { value: "send_email", label: "Envoyer courriel", category: "Emails" },
  { value: "reassign_task", label: "Réassigner tâche", category: "Assignation" },
  { value: "reassign_step", label: "Réassigner étape", category: "Assignation" },
  { value: "set_task_health", label: "Définir santé tâche", category: "États" },
  { value: "set_task_status", label: "Définir statut tâche", category: "États" },
  { value: "create_subtask", label: "Créer sous-tâche", category: "Automatisation" },
  { value: "pause_task", label: "Mettre en pause", category: "Automatisation" },
  { value: "complete_task", label: "Compléter tâche", category: "Automatisation" },
];

// Trigger event options
const TRIGGER_EVENT_OPTIONS = [
  { value: "task_created", label: "Tâche créée" },
  { value: "step_started", label: "Étape démarrée" },
  { value: "activity_completed", label: "Activité complétée" },
  { value: "step_completed", label: "Étape complétée" },
  { value: "step_overdue", label: "Étape en retard" },
  { value: "task_overdue", label: "Tâche en retard" },
  { value: "manual", label: "Déclenchement manuel (ex: l'utilisateur clique un bouton)" },
];

function TriggerActionParams({
  actionType,
  payload,
  onChange,
}: {
  actionType: TriggerActionType;
  payload: Record<string, unknown>;
  onChange: (p: Record<string, unknown>) => void;
}) {
  const p = payload || {};
  const set = (key: string, value: unknown) => onChange({ ...p, [key]: value });
  if (actionType === "send_email") {
    const recipientLabel = (t: string) => ({ client: "Client", assignee: "Assigné", role: "Rôle spécifique", user: "Utilisateur précis" }[t] || "Client");
    const roleLabel = (r: string) => ({ to: "Principal (destinataire)", cc: "CC", bcc: "CCI" }[r] || "Principal (destinataire)");
    let recipients = (p.email_recipients as Array<{ recipient_type: string; role: string }>) ?? [];
    if (recipients.length === 0) {
      recipients = [{ recipient_type: (p.recipient_type as string) || "client", role: "to" }];
    }
    const updateRecipients = (next: Array<{ recipient_type: string; role: string }>) => set("email_recipients", next);
    const addRecipient = () => updateRecipients([...recipients, { recipient_type: "client", role: "to" }]);
    const removeRecipient = (i: number) => updateRecipients(recipients.filter((_, idx) => idx !== i));
    const updateRecipient = (i: number, field: "recipient_type" | "role", value: string) => {
      const next = [...recipients];
      next[i] = { ...next[i], [field]: value };
      updateRecipients(next);
    };
    return (
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-medium text-text">Paramètres — Envoyer courriel</h4>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Destinataires (Principal, CC, CCI)</label>
          <div className="space-y-2">
            {recipients.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Select
                  label=""
                  hideLabel
                  value={recipientLabel(r.recipient_type)}
                  onChange={(v) => updateRecipient(i, "recipient_type", v === "Client" ? "client" : v === "Assigné" ? "assignee" : v === "Rôle spécifique" ? "role" : "user")}
                  options={["Client", "Assigné", "Rôle spécifique", "Utilisateur précis"]}
                />
                <Select
                  label=""
                  hideLabel
                  value={roleLabel(r.role)}
                  onChange={(v) => updateRecipient(i, "role", v === "Principal (destinataire)" ? "to" : v === "CC" ? "cc" : "bcc")}
                  options={["Principal (destinataire)", "CC", "CCI"]}
                />
                {recipients.length > 1 && (
                  <IconButton icon={<X size={14} />} ariaLabel="Retirer" intent="ghost" size="sm" onClick={() => removeRecipient(i)} />
                )}
              </div>
            ))}
            <Button label="Ajouter un destinataire" intent="ghost" size="sm" onClick={addRecipient} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Template courriel</label>
          <Select
            label=""
            hideLabel
            value={(p.template_code as string) || "DEMO_STEP_ALERT"}
            onChange={(v) => set("template_code", v)}
            options={["DEMO_STEP_ALERT"]}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Envoi</label>
          <Select
            label=""
            hideLabel
            value={(p.send_mode as string) === "delay_hours" ? "+X heures" : (p.send_mode as string) === "delay_days" ? "+X jours" : "Immédiat"}
            onChange={(v) => set("send_mode", v === "Immédiat" ? "immediate" : v === "+X heures" ? "delay_hours" : "delay_days")}
            options={["Immédiat", "+X heures", "+X jours"]}
          />
          {((p.send_mode as string) === "delay_hours" || (p.send_mode as string) === "delay_days") && (
            <input
              type="number"
              min={1}
              className="mt-1 h-9 w-24 rounded-md border border-border bg-surface px-2 text-sm"
              placeholder={(p.send_mode as string) === "delay_hours" ? "Heures" : "Jours"}
              value={(p.delay_value as number) ?? ""}
              onChange={(e) => set("delay_value", e.target.value ? parseInt(e.target.value, 10) : undefined)}
            />
          )}
        </div>
      </div>
    );
  }
  if (actionType === "create_alert") {
    return (
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-medium text-text">Paramètres — Créer alerte</h4>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Destinataire</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Qui reçoit l'alerte" value={(p.recipient as string) ?? ""} onChange={(e) => set("recipient", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Niveau</label>
          <Select label="" hideLabel value={((l: string) => (l ? String(l).charAt(0).toUpperCase() + String(l).slice(1) : "Info"))((p.level as string) || "info")} onChange={(v) => set("level", v.toLowerCase())} options={["Info", "Warning", "Error", "Critical"]} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Message</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Message ou template" value={(p.message as string) ?? ""} onChange={(e) => set("message", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Lien</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="URL ou chemin" value={(p.link as string) ?? ""} onChange={(e) => set("link", e.target.value)} />
        </div>
      </div>
    );
  }
  if (actionType === "reassign_task" || actionType === "reassign_step") {
    return (
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-medium text-text">Paramètres — Réassigner</h4>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Nouvelle assignation</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Rôle ou utilisateur" value={(p.new_assignment as string) ?? ""} onChange={(e) => set("new_assignment", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Condition</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Quand réassigner (optionnel)" value={(p.condition as string) ?? ""} onChange={(e) => set("condition", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Raison (audit)</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Raison" value={(p.reason as string) ?? ""} onChange={(e) => set("reason", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Fallback (si rôle vide)</label>
          <input type="text" className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Optionnel" value={(p.fallback as string) ?? ""} onChange={(e) => set("fallback", e.target.value)} />
        </div>
      </div>
    );
  }
  if (actionType === "schedule_reminder") {
    return (
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-medium text-text">Paramètres — Planifier rappel</h4>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Déclenchement</label>
          <Select label="" hideLabel value={(p.trigger_type as string) === "date" ? "À une date précise" : "Dans X temps"} onChange={(v) => set("trigger_type", v === "À une date précise" ? "date" : "delay")} options={["Dans X temps", "À une date précise"]} />
          {(p.trigger_type as string) !== "date" && (
            <input type="number" min={1} className="mt-1 h-9 w-20 rounded-md border border-border bg-surface px-2 text-sm" placeholder="Jours" value={(p.delay_days as number) ?? ""} onChange={(e) => set("delay_days", e.target.value ? parseInt(e.target.value, 10) : undefined)} />
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Canal</label>
          <Select label="" hideLabel value={(p.channel as string) === "alert" ? "Alerte" : "Courriel"} onChange={(v) => set("channel", v === "Alerte" ? "alert" : "email")} options={["Courriel", "Alerte"]} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="stop_if_completed" checked={!!(p.stop_if_completed ?? true)} onChange={(e) => set("stop_if_completed", e.target.checked)} className="rounded" />
          <label htmlFor="stop_if_completed" className="text-xs text-muted">Arrêt automatique si complété</label>
        </div>
      </div>
    );
  }
  return (
    <div className="border-t border-border pt-4">
      <p className="text-xs text-muted">Aucun paramètre supplémentaire pour cette action.</p>
    </div>
  );
}

type StepEditorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: ProcessStepTemplate | null;
  /** Ordre suggéré pour une nouvelle étape (ex: suivant après les étapes existantes). */
  nextOrderIndex?: number;
  activities: ProcessActivityTemplate[];
  triggers: ProcessTriggerTemplate[];
  onSave: (data: {
    order_index: number;
    title: string;
    description: string | null;
    sla_days: number | null;
    completion_policy: CompletionPolicy;
    entry_conditions?: StepConditions | null;
    exit_conditions?: StepConditions | null;
    activities: Array<{
      id?: string;
      title: string;
      description: string;
      is_required: boolean;
      activity_type: ActivityType;
      order_index: number;
    }>;
    triggers: Array<{
      id?: string;
      title: string;
      description: string;
      event_type: TriggerEventType;
      action_type: TriggerActionType;
      action_payload: Record<string, any>;
    }>;
  }) => Promise<void>;
};

export function StepEditorModal({
  open,
  onOpenChange,
  step,
  nextOrderIndex,
  activities: initialActivities,
  triggers: initialTriggers,
  onSave,
}: StepEditorModalProps) {
  const [form, setForm] = useState({
    order_index: step?.order_index.toString() || (nextOrderIndex != null ? String(nextOrderIndex) : ""),
    title: step?.title || "",
    description: step?.description || "",
    sla_days: step?.sla_days?.toString() || "",
    completion_policy: (step?.completion_policy || "all_required") as CompletionPolicy,
    entry_conditions: step?.entry_conditions || null,
    exit_conditions: step?.exit_conditions || null,
  });
  const [localActivities, setLocalActivities] = useState<Array<{
    id?: string;
    title: string;
    description: string;
    is_required: boolean;
    activity_type: ActivityType;
    order_index: number;
    checklist_config?: { email_template_code?: string | null; email_recipient_type?: string } | null;
    cost_activity_config?: { cost_source?: string; quantity_formula?: string; is_mandatory?: boolean; allow_override?: boolean } | null;
    assignee_activity_config?: AssigneeActivityConfig | null;
    activity_ui_config?: ActivityUIConfig | null;
  }>>([]);
  const [localTriggers, setLocalTriggers] = useState<Array<{
    id?: string;
    title: string;
    description: string;
    event_type: TriggerEventType;
    action_type: TriggerActionType;
    action_payload: Record<string, any>;
  }>>([]);

  // Initialize activities and triggers when step or props change
  useEffect(() => {
    if (open) {
      const defaultOrder = step != null ? step.order_index.toString() : (nextOrderIndex != null ? String(nextOrderIndex) : "1");
      setForm({
        order_index: defaultOrder,
        title: step?.title || "",
        description: step?.description || "",
        sla_days: step?.sla_days?.toString() || "",
        completion_policy: (step?.completion_policy || "all_required") as CompletionPolicy,
        entry_conditions: step?.entry_conditions || null,
        exit_conditions: step?.exit_conditions || null,
      });
      setLocalActivities(
        initialActivities.map((a, idx) => ({
          id: a.id,
          title: a.title,
          description: a.description || "",
          is_required: a.is_required,
          activity_type: a.activity_type,
          order_index: idx + 1,
          checklist_config: (a as { checklist_config?: { email_template_code?: string | null; email_recipient_type?: string; email_recipients?: Array<{ recipient_type: string; role: string }> } | null }).checklist_config ?? undefined,
          cost_activity_config: (a as { cost_activity_config?: { cost_source?: string; quantity_formula?: string; is_mandatory?: boolean; allow_override?: boolean } | null }).cost_activity_config ?? undefined,
          assignee_activity_config: (a as { assignee_activity_config?: AssigneeActivityConfig | null }).assignee_activity_config ?? undefined,
          activity_ui_config: (a as { activity_ui_config?: ActivityUIConfig | null }).activity_ui_config ?? undefined,
        }))
      );
      setLocalTriggers(
        initialTriggers.map((t) => ({
          id: t.id,
          title: TRIGGER_ACTION_OPTIONS.find((opt) => opt.value === t.action_type)?.label || "",
          description: "",
          event_type: t.event_type,
          action_type: t.action_type,
          action_payload: t.action_payload || {},
        }))
      );
    }
  }, [open, step, nextOrderIndex, initialActivities, initialTriggers]);

  const [emailTemplateOptions, setEmailTemplateOptions] = useState<Array<{ value: string; label: string }>>(EMAIL_TEMPLATE_OPTIONS_DEFAULT);
  // Load email templates from API when modal opens (for activity "Envoyer courriel")
  useEffect(() => {
    if (!open) return;
    setEmailTemplateOptions(EMAIL_TEMPLATE_OPTIONS_DEFAULT);
    listEmailTemplates()
      .then((res) => {
        const opts = [
          ...EMAIL_TEMPLATE_OPTIONS_DEFAULT,
          ...(res.items || []).map((t) => ({ value: t.code, label: t.name || t.code })),
        ];
        setEmailTemplateOptions(opts);
      })
      .catch(() => {
        // Keep default "Aucun template" on error
      });
    // Load ambassadors and custom roles for "Assigner un responsable" activity
    listAmbassadorOrgs({ page_size: 500 })
      .then((res: { items?: Array<{ organization_id?: string; organization?: { id: string; name: string }; name?: string }> }) => {
        const list = (res.items || []).map((item) => ({
          id: item.organization_id || item.organization?.id || "",
          name: item.organization?.name || item.name || item.organization_id || "—",
        })).filter((a) => a.id);
        setAmbassadors(list);
      })
      .catch(() => setAmbassadors([]));
    getAdminOrgId()
      .then((adminOrgId) => listCustomRoles(adminOrgId))
      .then((roles) => setCustomRoles(roles.map((r) => ({ id: r.id, name: r.name || r.id }))))
      .catch(() => setCustomRoles([]));
  }, [open]);

  const [editingActivityIndex, setEditingActivityIndex] = useState<number | null>(null);
  const [editingTriggerIndex, setEditingTriggerIndex] = useState<number | null>(null);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [isAddingTrigger, setIsAddingTrigger] = useState(false);
  const [activityForm, setActivityForm] = useState({
    title: "",
    description: "",
    is_required: true,
    activity_type: "other" as ActivityType,
    email_template_code: "" as string,
    email_recipient_type: "client" as string,
    email_recipients: [] as Array<{ recipient_type: string; role: string }>,
    cost_activity_config: null as { cost_source?: string; quantity_formula?: string; is_mandatory?: boolean; allow_override?: boolean } | null,
    assignee_activity_config: null as AssigneeActivityConfig | null,
    activity_ui_config: null as ActivityUIConfig | null,
  });
  const [ambassadors, setAmbassadors] = useState<Array<{ id: string; name: string }>>([]);
  const [customRoles, setCustomRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [triggerForm, setTriggerForm] = useState({
    title: "",
    description: "",
    event_type: "step_started" as TriggerEventType,
    action_type: "create_alert" as TriggerActionType,
    action_payload: {} as Record<string, any>,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<"general" | "activities" | "triggers" | "entry" | "exit" | "completion">("general");
  const [alertsCollapsed, setAlertsCollapsed] = useState(false);

  const sectionLabels: Record<typeof activeSection, string> = {
    general: "Information générale",
    activities: "Activités",
    triggers: "Triggers",
    entry: "Condition d'entrée",
    exit: "Condition de sortie",
    completion: "Politique de complétion",
  };

  const alerts = useMemo(() => {
    const list: string[] = [];
    if (!form.title?.trim()) list.push("Titre de l'étape requis");
    if (localActivities.length === 0) list.push("Au moins une activité recommandée");
    const requiredActivities = localActivities.filter((a) => a.is_required);
    if (localActivities.length > 0 && requiredActivities.length === 0) list.push("Au moins une activité obligatoire recommandée");
    return list;
  }, [form.title, localActivities]);

  const isStepOperational = alerts.length === 0;

  const handleAddActivity = () => {
    setEditingActivityIndex(null);
    setIsAddingActivity(true);
    setActivityForm({
      title: "",
      description: "",
      is_required: true,
      activity_type: "other",
      email_template_code: "",
      email_recipient_type: "client",
      email_recipients: [],
      cost_activity_config: null,
      assignee_activity_config: null,
      activity_ui_config: null,
    });
    setErrors({});
  };

  const handleSaveActivity = () => {
    const trimmedTitle = activityForm.title?.trim() || "";
    if (!trimmedTitle) {
      setErrors({ activity: "Le titre de l'activité est requis" });
      return;
    }
    const checklist_config =
      activityForm.activity_type === "send_email_manual"
        ? {
            email_template_code: activityForm.email_template_code?.trim() || null,
            email_recipient_type: activityForm.email_recipient_type || "client",
            email_recipients: (activityForm.email_recipients?.length ? activityForm.email_recipients : [{ recipient_type: activityForm.email_recipient_type || "client", role: "to" }]) as Array<{ recipient_type: string; role: string }>,
          }
        : undefined;
    const cost_activity_config =
      activityForm.activity_type === "add_operational_cost" && activityForm.cost_activity_config
        ? activityForm.cost_activity_config
        : undefined;
    const assignee_activity_config =
      activityForm.activity_type === "assign_responsible" && activityForm.assignee_activity_config
        ? activityForm.assignee_activity_config
        : undefined;
    const hasUiConfig =
      activityForm.activity_ui_config &&
      ((activityForm.activity_ui_config.fields?.length ?? 0) > 0 ||
        (activityForm.activity_ui_config.action_rules?.length ?? 0) > 0 ||
        (activityForm.activity_ui_config.buttons?.length ?? 0) > 0);
    const activity_ui_config = hasUiConfig ? activityForm.activity_ui_config : undefined;
    const newActivity = {
      ...(editingActivityIndex !== null && localActivities[editingActivityIndex].id != null ? { id: localActivities[editingActivityIndex].id } : {}),
      title: trimmedTitle,
      description: activityForm.description ?? "",
      is_required: activityForm.is_required,
      activity_type: activityForm.activity_type,
      order_index: editingActivityIndex !== null ? localActivities[editingActivityIndex].order_index : localActivities.length + 1,
      ...(checklist_config != null ? { checklist_config } : {}),
      ...(cost_activity_config != null ? { cost_activity_config } : {}),
      ...(assignee_activity_config != null ? { assignee_activity_config } : {}),
      ...(activity_ui_config != null ? { activity_ui_config } : {}),
    };
    if (editingActivityIndex !== null) {
      const updated = [...localActivities];
      updated[editingActivityIndex] = newActivity;
      setLocalActivities(updated);
    } else {
      setLocalActivities([...localActivities, newActivity]);
    }
    setEditingActivityIndex(null);
    setIsAddingActivity(false);
    setActivityForm({ title: "", description: "", is_required: true, activity_type: "other", email_template_code: "", email_recipient_type: "client", email_recipients: [], cost_activity_config: null, assignee_activity_config: null, activity_ui_config: null });
    setErrors({});
  };

  const handleDeleteActivity = (index: number) => {
    const updated = localActivities.filter((_, i) => i !== index);
    // Reorder remaining activities
    updated.forEach((a, i) => {
      a.order_index = i + 1;
    });
    setLocalActivities(updated);
  };

  const handleMoveActivity = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === localActivities.length - 1) return;
    const updated = [...localActivities];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated[index].order_index = index + 1;
    updated[newIndex].order_index = newIndex + 1;
    setLocalActivities(updated);
  };

  const handleAddTrigger = () => {
    setEditingTriggerIndex(null);
    setIsAddingTrigger(true);
    setTriggerForm({
      title: "",
      description: "",
      event_type: "step_started",
      action_type: "create_alert",
      action_payload: {},
    });
    setErrors({});
  };

  const handleSaveTrigger = () => {
    const trimmedTitle = triggerForm.title?.trim() || "";
    if (!trimmedTitle) {
      setErrors({ trigger: "Le titre du trigger est requis" });
      return;
    }
    const newTrigger = { ...triggerForm, title: trimmedTitle };
    if (editingTriggerIndex !== null) {
      const updated = [...localTriggers];
      updated[editingTriggerIndex] = newTrigger;
      setLocalTriggers(updated);
    } else {
      setLocalTriggers([...localTriggers, newTrigger]);
    }
    setEditingTriggerIndex(null);
    setIsAddingTrigger(false);
    setTriggerForm({ title: "", description: "", event_type: "step_started", action_type: "create_alert", action_payload: {} });
    setErrors({});
  };

  const handleDeleteTrigger = (index: number) => {
    setLocalTriggers(localTriggers.filter((_, i) => i !== index));
  };

  const runSave = async (closeAfter: boolean) => {
    const newErrors: Record<string, string> = {};
    if (!form.title.trim()) {
      newErrors.title = "Le titre est requis";
    }
    if (!form.order_index.trim()) {
      newErrors.order_index = "L'ordre est requis";
    } else {
      const orderNum = parseInt(form.order_index);
      if (isNaN(orderNum) || orderNum < 1) {
        newErrors.order_index = "L'ordre doit être un nombre positif";
      }
    }
    if (form.sla_days && isNaN(parseInt(form.sla_days))) {
      newErrors.sla_days = "Le SLA doit être un nombre valide";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      await onSave({
        order_index: parseInt(form.order_index),
        title: form.title,
        description: form.description || null,
        sla_days: form.sla_days ? parseInt(form.sla_days) : null,
        completion_policy: form.completion_policy,
        entry_conditions: form.entry_conditions,
        exit_conditions: form.exit_conditions,
        activities: localActivities,
        triggers: localTriggers,
      });
      if (closeAfter) onOpenChange(false);
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    }
  };

  const handleSubmit = () => runSave(false);
  const handleActivate = () => runSave(true);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={step ? "Modifier l'étape" : "Nouvelle étape"}
      showDefaultButtons={false}
      showCloseButton={true}
      size="3xl"
    >
      <div className="flex flex-col max-h-[85vh]">
        {/* Statut + actions Enregistrer / Activer */}
        <div className="mb-4 rounded-lg border border-border bg-surface-muted/50 overflow-hidden">
          <div className="flex items-center justify-between gap-4 p-3 flex-wrap">
            <button
              type="button"
              onClick={() => setAlertsCollapsed(!alertsCollapsed)}
              className="flex items-center gap-2 text-left text-sm font-medium text-text hover:opacity-80"
            >
              {alertsCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              <Info size={16} className={isStepOperational ? "text-success" : "text-warning"} />
              {isStepOperational ? "Étape opérationnelle" : `À compléter : ${alerts.join(", ")}`}
            </button>
            <div className="flex items-center gap-2">
              <Button
                label="Enregistrer"
                intent="default"
                size="sm"
                onClick={handleSubmit}
              />
              <Button
                label="Activer"
                intent="primary"
                size="sm"
                state={!isStepOperational ? "disabled" : "default"}
                onClick={handleActivate}
              />
            </div>
          </div>
          {!alertsCollapsed && (
            <div className="px-3 pb-3 pt-0 text-xs text-muted space-y-1 border-t border-border">
              {alerts.length > 0 ? (
                <>
                  {alerts.map((a, i) => (
                    <div key={i}>• {a}</div>
                  ))}
                  <p className="mt-2 text-muted">Vous pouvez enregistrer et revenir plus tard. Le bouton « Activer » sera disponible lorsque tout sera correct.</p>
                </>
              ) : (
                <p className="text-success">Rien à signaler. L'étape est prête à être utilisée.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-6 flex-1 min-h-0">
          {/* Side nav avec séparations visuelles */}
          <nav className="w-52 flex-shrink-0 border-r border-border pr-2 space-y-1">
            <div className="py-1">
              <button
                type="button"
                onClick={() => setActiveSection("general")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "general" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.general}
              </button>
            </div>
            <div className="border-t border-border my-2" />
            <div className="py-1">
              <button
                type="button"
                onClick={() => setActiveSection("activities")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "activities" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.activities}
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("triggers")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "triggers" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.triggers}
              </button>
            </div>
            <div className="border-t border-border my-2" />
            <div className="py-1">
              <button
                type="button"
                onClick={() => setActiveSection("entry")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "entry" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.entry}
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("exit")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "exit" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.exit}
              </button>
            </div>
            <div className="border-t border-border my-2" />
            <div className="py-1">
              <button
                type="button"
                onClick={() => setActiveSection("completion")}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  activeSection === "completion" ? "bg-primary/10 text-primary border-primary" : "border-transparent text-text hover:bg-surface-muted"
                }`}
              >
                {sectionLabels.completion}
              </button>
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-w-0">
            {activeSection === "general" && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text">Titre de l'étape *</label>
                  <Input
                    label=""
                    hideLabel
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: Préparation du matériel"
                    state={errors.title ? "error" : "default"}
                  />
                  {errors.title && <p className="text-xs text-danger mt-1">{errors.title}</p>}
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text">Description / Objectifs</label>
                  <textarea
                    className="w-full min-h-[100px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Décrivez en détail ce qui doit être fait dans cette étape..."
                  />
                </div>
                <div className="border-t border-border pt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-text">Ordre dans le processus *</label>
<input
                      type="number"
                      min={1}
                      className="h-10 w-full rounded-md border border-border bg-surface px-3 text-text"
                      value={form.order_index}
                      onChange={(e) => setForm({ ...form, order_index: e.target.value })}
                      placeholder="1, 2, 3..."
                    />
                    {errors.order_index && <p className="text-xs text-danger mt-1">{errors.order_index}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-text">SLA (jours)</label>
                    <input
                      type="number"
                      min={0}
                      className="h-10 w-full rounded-md border border-border bg-surface px-3 text-text"
                      value={form.sla_days}
                      onChange={(e) => setForm({ ...form, sla_days: e.target.value })}
                      placeholder="Ex: 5"
                    />
                    {errors.sla_days && <p className="text-xs text-danger mt-1">{errors.sla_days}</p>}
                  </div>
                </div>
              </div>
            )}

            {activeSection === "activities" && (
        <div className="space-y-4">
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-text">Activités</h3>
            <Button
              label="Ajouter une activité"
              intent="default"
              size="sm"
              onClick={handleAddActivity}
            />
          </div>
          {editingActivityIndex === null && !isAddingActivity ? (
            <div className="space-y-2">
              {localActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune activité définie</p>
              ) : (
                localActivities.map((activity, index) => (
                  <div key={index} className="p-3 border border-border rounded-lg bg-surface flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{activity.title}</span>
                        {activity.is_required && (
                          <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded">Requis</span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {ACTIVITY_TYPE_OPTIONS.find((opt) => opt.value === activity.activity_type)?.label || activity.activity_type}
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-xs text-muted-foreground mt-1">{activity.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <IconButton
                        icon={<ArrowUp size={14} />}
                        ariaLabel="Déplacer vers le haut"
                        intent="ghost"
                        size="sm"
                        onClick={() => handleMoveActivity(index, "up")}
                      />
                      <IconButton
                        icon={<ArrowDown size={14} />}
                        ariaLabel="Déplacer vers le bas"
                        intent="ghost"
                        size="sm"
                        onClick={() => handleMoveActivity(index, "down")}
                      />
                      <IconButton
                        icon={<Pencil size={14} />}
                        ariaLabel="Modifier"
                        intent="ghost"
                        size="sm"
                      onClick={() => {
                        setEditingActivityIndex(index);
                        setIsAddingActivity(false);
                        setActivityForm({
                          title: activity.title,
                          description: activity.description,
                          is_required: activity.is_required,
                          activity_type: activity.activity_type,
                          email_template_code: activity.checklist_config?.email_template_code ?? "",
                          email_recipient_type: activity.checklist_config?.email_recipient_type ?? "client",
                          email_recipients: (activity.checklist_config as { email_recipients?: Array<{ recipient_type: string; role: string }> })?.email_recipients?.length
                            ? (activity.checklist_config as { email_recipients: Array<{ recipient_type: string; role: string }> }).email_recipients
                            : [],
                          cost_activity_config: (activity as { cost_activity_config?: typeof activityForm.cost_activity_config }).cost_activity_config ?? null,
                          assignee_activity_config: (activity as { assignee_activity_config?: AssigneeActivityConfig | null }).assignee_activity_config ?? null,
                          activity_ui_config: (activity as { activity_ui_config?: ActivityUIConfig | null }).activity_ui_config ?? null,
                        });
                      }}
                      />
                      <IconButton
                        icon={<Trash2 size={14} />}
                        ariaLabel="Supprimer"
                        intent="ghost"
                        size="sm"
                        onClick={() => handleDeleteActivity(index)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="p-4 border border-border rounded-lg bg-surface-muted space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Nom de l'activité *</label>
                <Input
                  value={activityForm.title}
                  onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                  placeholder="Ex: Appeler le client"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Type d'activité</label>
                <Select
                  label=""
                  hideLabel={true}
                  value={ACTIVITY_TYPE_OPTIONS.find((opt) => opt.value === activityForm.activity_type)?.label || ""}
                  onChange={(value) => {
                    const option = ACTIVITY_TYPE_OPTIONS.find((opt) => opt.label === value);
                    if (option) {
                      const nextType = option.value as ActivityType;
                      setActivityForm({
                        ...activityForm,
                        activity_type: nextType,
                        cost_activity_config: nextType === "add_operational_cost" ? (activityForm.cost_activity_config ?? {}) : null,
                        assignee_activity_config: nextType === "assign_responsible"
                          ? (activityForm.assignee_activity_config ?? { assignee_source: "client_ambassador" })
                          : null,
                      });
                    }
                  }}
                  options={ACTIVITY_TYPE_OPTIONS.map((opt) => opt.label)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Description (facultatif)</label>
                <textarea
                  className="w-full min-h-[60px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text resize-y"
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  placeholder="Description de l'activité..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={activityForm.is_required}
                  onChange={(e) => setActivityForm({ ...activityForm, is_required: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_required" className="text-sm font-medium">
                  Activité obligatoire
                </label>
              </div>

              {/* Interface utilisateur de l'activité */}
              <div className="border-t border-border pt-3 space-y-4">
                <h4 className="text-sm font-medium text-text">Interface utilisateur de l&apos;activité</h4>
                <p className="text-xs text-muted-foreground">
                  Définir les champs à remplir par l&apos;utilisateur et les règles conditionnelles liées aux choix.
                </p>

                {/* Champs */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text">Champs à remplir</label>
                    <Button
                      label="Ajouter un champ"
                      intent="default"
                      size="sm"
                      onClick={() => {
                        const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: activityForm.activity_ui_config?.conditional_rules ?? [] };
                        const fields = [...(ui.fields ?? []), { id: "f_" + Math.random().toString(36).slice(2, 9), label: "", type: "text" as ActivityUIFieldType, required: false, placeholder: null, choices: null, default_value: null, presentation: "dropdown" as const }];
                        setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                      }}
                      icon={<Plus size={14} />}
                    />
                  </div>
                  {((activityForm.activity_ui_config?.fields) ?? []).map((field, idx) => (
                    <div key={field.id} className="p-3 border border-border rounded bg-surface-subtle space-y-2 mb-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        <Input
                          value={field.label}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                            const fields = [...(ui.fields ?? [])];
                            fields[idx] = { ...field, label: e.target.value };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                          }}
                          placeholder="Libellé du champ"
                          className="flex-1 min-w-[120px]"
                        />
                        <select
                          value={field.type}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                            const fields = [...(ui.fields ?? [])];
                            const nextType = e.target.value as ActivityUIFieldType;
                            fields[idx] = { ...field, type: nextType, choices: nextType === "select" ? (field.choices ?? []) : undefined };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                          }}
                          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                        >
                          <option value="text">Texte</option>
                          <option value="textarea">Zone de texte</option>
                          <option value="number">Nombre</option>
                          <option value="select">Liste déroulante</option>
                          <option value="checkbox">Case à cocher</option>
                          <option value="date">Date</option>
                          <option value="email">Courriel</option>
                        </select>
                        <label className="flex items-center gap-1 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={field.required ?? false}
                            onChange={(e) => {
                              const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                              const fields = [...(ui.fields ?? [])];
                              fields[idx] = { ...field, required: e.target.checked };
                              setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                            }}
                            className="rounded"
                          />
                          <span className="text-xs">Requis</span>
                        </label>
                        <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer" intent="ghost" size="sm" onClick={() => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                          const fields = (ui.fields ?? []).filter((_, i) => i !== idx);
                          const conditional_rules = (ui.conditional_rules ?? []).filter((r) => r.source_field_id !== field.id && r.target_field_id !== field.id);
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields, conditional_rules } });
                        }} />
                      </div>
                      {(field.type === "text" || field.type === "textarea" || field.type === "email") && (
                        <Input
                          value={field.placeholder ?? ""}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                            const fields = [...(ui.fields ?? [])];
                            fields[idx] = { ...field, placeholder: e.target.value || null };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                          }}
                          placeholder="Placeholder (optionnel)"
                          className="text-sm"
                        />
                      )}
                      {field.type === "select" && (
                        <div className="pl-2 border-l-2 border-border space-y-2">
                          <div>
                            <span className="text-xs font-medium text-text">Présentation</span>
                            <select
                              value={field.presentation ?? "dropdown"}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                                const fields = [...(ui.fields ?? [])];
                                fields[idx] = { ...field, presentation: e.target.value as "dropdown" | "radio" };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                              }}
                              className="ml-2 h-8 rounded-md border border-border bg-surface px-2 text-sm text-text"
                            >
                              <option value="dropdown">Liste déroulante</option>
                              <option value="radio">Boutons radio</option>
                            </select>
                          </div>
                          <span className="text-xs font-medium text-text">Choix possibles</span>
                          {(field.choices ?? []).map((choice, cIdx) => (
                            <div key={cIdx} className="flex gap-2 items-center">
                              <Input value={choice.value} onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                                const fields = [...(ui.fields ?? [])];
                                const choices = [...(fields[idx].choices ?? [])];
                                choices[cIdx] = { ...choices[cIdx], value: e.target.value };
                                fields[idx] = { ...field, choices };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                              }} placeholder="Valeur" className="flex-1 text-sm" />
                              <Input value={choice.label} onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                                const fields = [...(ui.fields ?? [])];
                                const choices = [...(fields[idx].choices ?? [])];
                                choices[cIdx] = { ...choices[cIdx], label: e.target.value };
                                fields[idx] = { ...field, choices };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                              }} placeholder="Libellé" className="flex-1 text-sm" />
                              <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer" intent="ghost" size="sm" onClick={() => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                                const fields = [...(ui.fields ?? [])];
                                const choices = (fields[idx].choices ?? []).filter((_, i) => i !== cIdx);
                                fields[idx] = { ...field, choices };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                              }} />
                            </div>
                          ))}
                          <Button label="Ajouter un choix" intent="ghost" size="sm" icon={<Plus size={12} />} onClick={() => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                            const fields = [...(ui.fields ?? [])];
                            const choices = [...(fields[idx].choices ?? []), { value: "", label: "" }];
                            fields[idx] = { ...field, choices };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                          }} />
                          <div className="pt-1">
                            <span className="text-xs font-medium text-text">Valeur par défaut</span>
                            <select
                              value={typeof field.default_value === "string" ? field.default_value : ""}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                                const fields = [...(ui.fields ?? [])];
                                fields[idx] = { ...field, default_value: e.target.value || null };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                              }}
                              className="ml-2 h-8 rounded-md border border-border bg-surface px-2 text-sm text-text"
                            >
                              <option value="">— Aucune —</option>
                              {(field.choices ?? []).filter((c) => c.value).map((c) => (
                                <option key={c.value} value={c.value}>{c.label || c.value}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {(field.type === "text" || field.type === "textarea" || field.type === "email" || field.type === "number" || field.type === "date") && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-text">Valeur par défaut</span>
                          <Input
                            value={field.default_value != null ? String(field.default_value) : ""}
                            onChange={(e) => {
                              const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                              const fields = [...(ui.fields ?? [])];
                              const v = e.target.value;
                              fields[idx] = { ...field, default_value: field.type === "number" ? (v === "" ? null : Number(v)) : (v || null) };
                              setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                            }}
                            placeholder="Optionnel"
                            className="flex-1 max-w-[200px] text-sm"
                            type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                          />
                        </div>
                      )}
                      {field.type === "checkbox" && (
                        <label className="flex items-center gap-2 text-sm">
                          <span className="text-xs font-medium text-text">Coché par défaut</span>
                          <input
                            type="checkbox"
                            checked={field.default_value === true}
                            onChange={(e) => {
                              const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                              const fields = [...(ui.fields ?? [])];
                              fields[idx] = { ...field, default_value: e.target.checked };
                              setActivityForm({ ...activityForm, activity_ui_config: { ...ui, fields } });
                            }}
                            className="rounded"
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>

                {/* Règles conditionnelles (visibilité) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text">Règles conditionnelles</label>
                    <Button
                      label="Ajouter une règle"
                      intent="default"
                      size="sm"
                      onClick={() => {
                        const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                        const rules = [...(ui.conditional_rules ?? []), { id: "r_" + Math.random().toString(36).slice(2, 9), source_field_id: "", operator: "equals" as const, value: null, target_field_id: "", action: "show" as const }];
                        setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                      }}
                      icon={<Plus size={14} />}
                    />
                  </div>
                  {((activityForm.activity_ui_config?.conditional_rules) ?? []).map((rule, idx) => (
                    <div key={rule.id} className="p-3 border border-border rounded bg-surface-subtle flex flex-wrap gap-2 items-center mb-2">
                      <span className="text-xs text-muted-foreground">Si</span>
                      <select
                        value={rule.source_field_id}
                        onChange={(e) => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                          const rules = [...(ui.conditional_rules ?? [])];
                          rules[idx] = { ...rule, source_field_id: e.target.value };
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                        }}
                        className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text min-w-[100px]"
                      >
                        <option value="">— Champ —</option>
                        {((activityForm.activity_ui_config?.fields) ?? []).map((f) => (
                          <option key={f.id} value={f.id}>{f.label || f.id}</option>
                        ))}
                      </select>
                      <select
                        value={rule.operator}
                        onChange={(e) => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                          const rules = [...(ui.conditional_rules ?? [])];
                          rules[idx] = { ...rule, operator: e.target.value as ActivityUIConditionalRule["operator"] };
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                        }}
                        className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                      >
                        <option value="equals">égal à</option>
                        <option value="not_equals">différent de</option>
                        <option value="contains">contient</option>
                        <option value="empty">est vide</option>
                      </select>
                      {rule.operator !== "empty" && (
                        <Input
                          value={rule.value ?? ""}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                            const rules = [...(ui.conditional_rules ?? [])];
                            rules[idx] = { ...rule, value: e.target.value };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                          }}
                          placeholder="Valeur"
                          className="w-24 text-sm"
                        />
                      )}
                      <span className="text-xs text-muted-foreground">→</span>
                      <select
                        value={rule.target_field_id}
                        onChange={(e) => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                          const rules = [...(ui.conditional_rules ?? [])];
                          rules[idx] = { ...rule, target_field_id: e.target.value };
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                        }}
                        className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text min-w-[100px]"
                      >
                        <option value="">— Champ cible —</option>
                        {((activityForm.activity_ui_config?.fields) ?? []).map((f) => (
                          <option key={f.id} value={f.id}>{f.label || f.id}</option>
                        ))}
                      </select>
                      <select
                        value={rule.action}
                        onChange={(e) => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                          const rules = [...(ui.conditional_rules ?? [])];
                          rules[idx] = { ...rule, action: e.target.value as ActivityUIConditionalRule["action"] };
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                        }}
                        className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                      >
                        <option value="show">afficher</option>
                        <option value="hide">masquer</option>
                        <option value="require">rendre obligatoire</option>
                        <option value="optional">rendre optionnel</option>
                      </select>
                      <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer la règle" intent="ghost" size="sm" onClick={() => {
                        const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [] };
                        const rules = (ui.conditional_rules ?? []).filter((_, i) => i !== idx);
                        setActivityForm({ ...activityForm, activity_ui_config: { ...ui, conditional_rules: rules } });
                      }} />
                    </div>
                  ))}
                </div>

                {/* Règles conditionnelles (actions système) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text">Règles conditionnelles (actions système)</label>
                    <Button
                      label="Ajouter une règle"
                      intent="default"
                      size="sm"
                      onClick={() => {
                        const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [] };
                        const rules = [...(ui.action_rules ?? []), { id: "ar_" + Math.random().toString(36).slice(2, 9), source_field_id: "", operator: "equals" as const, value: null, actions: [] }];
                        setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                      }}
                      icon={<Plus size={14} />}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Si un champ a une valeur → exécuter les actions (coût, email, inventaire).</p>
                  {((activityForm.activity_ui_config?.action_rules) ?? []).map((rule, idx) => (
                    <div key={rule.id} className="p-3 border border-border rounded bg-surface-subtle space-y-2 mb-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-muted-foreground">Si</span>
                        <select
                          value={rule.source_field_id}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [] };
                            const rules = [...(ui.action_rules ?? [])];
                            rules[idx] = { ...rule, source_field_id: e.target.value };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                          }}
                          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text min-w-[100px]"
                        >
                          <option value="">— Champ —</option>
                          {((activityForm.activity_ui_config?.fields) ?? []).map((f) => (
                            <option key={f.id} value={f.id}>{f.label || f.id}</option>
                          ))}
                        </select>
                        <select
                          value={rule.operator}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [] };
                            const rules = [...(ui.action_rules ?? [])];
                            rules[idx] = { ...rule, operator: e.target.value as ActivityUIActionRule["operator"] };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                          }}
                          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                        >
                          <option value="equals">égal à</option>
                          <option value="not_equals">différent de</option>
                          <option value="contains">contient</option>
                          <option value="empty">est vide</option>
                        </select>
                        {rule.operator !== "empty" && (
                          <Input
                            value={rule.value ?? ""}
                            onChange={(e) => {
                              const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [] };
                              const rules = [...(ui.action_rules ?? [])];
                              rules[idx] = { ...rule, value: e.target.value };
                              setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                            }}
                            placeholder="Valeur"
                            className="w-24 text-sm"
                          />
                        )}
                        <span className="text-xs text-muted-foreground">→ exécuter actions</span>
                        <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer" intent="ghost" size="sm" onClick={() => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [] };
                          const rules = (ui.action_rules ?? []).filter((_, i) => i !== idx);
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                        }} />
                      </div>
                      <div className="pl-2 border-l-2 border-border">
                        <span className="text-xs font-medium text-text">Actions</span>
                        {(rule.actions ?? []).map((action, aIdx) => (
                          <div key={aIdx} className="flex gap-2 items-center mt-1">
                            <select
                              value={action.type}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], action_rules: [] };
                                const rules = [...(ui.action_rules ?? [])];
                                const actions = [...(rules[idx].actions ?? [])];
                                actions[aIdx] = { type: e.target.value as ActivityUIAction["type"], params: action.params || {} };
                                rules[idx] = { ...rule, actions };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                              }}
                              className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                            >
                              <option value="add_operational_cost">Ajouter coût</option>
                              <option value="send_email">Envoyer courriel</option>
                              <option value="consume_inventory">Consommer inventaire</option>
                            </select>
                            <Input
                              value={action.type === "send_email" ? String((action.params as { template_code?: string })?.template_code ?? "") : action.type === "add_operational_cost" ? String((action.params as { supplier_product_id?: string })?.supplier_product_id ?? "") : String((action.params as { supplier_product_id?: string })?.supplier_product_id ?? "")}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], action_rules: [] };
                                const rules = [...(ui.action_rules ?? [])];
                                const actions = [...(rules[idx].actions ?? [])];
                                const key = action.type === "send_email" ? "template_code" : "supplier_product_id";
                                actions[aIdx] = { ...action, params: { ...(action.params || {}), [key]: e.target.value } };
                                rules[idx] = { ...rule, actions };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                              }}
                              placeholder={action.type === "send_email" ? "Code template" : "ID produit/fournisseur"}
                              className="flex-1 text-sm"
                            />
                            <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer" intent="ghost" size="sm" onClick={() => {
                              const ui = activityForm.activity_ui_config ?? { fields: [], action_rules: [] };
                              const rules = [...(ui.action_rules ?? [])];
                              const actions = (rules[idx].actions ?? []).filter((_, i) => i !== aIdx);
                              rules[idx] = { ...rule, actions };
                              setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                            }} />
                          </div>
                        ))}
                        <Button
                          label="Ajouter action"
                          intent="ghost"
                          size="sm"
                          icon={<Plus size={12} />}
                          onClick={() => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], action_rules: [] };
                            const rules = [...(ui.action_rules ?? [])];
                            const actions = [...(rules[idx].actions ?? []), { type: "send_email" as const, params: { template_code: "" } }];
                            rules[idx] = { ...rule, actions };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, action_rules: rules } });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Boutons d'action */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text">Boutons d&apos;action</label>
                    <Button
                      label="Ajouter un bouton"
                      intent="default"
                      size="sm"
                      onClick={() => {
                        const ui = activityForm.activity_ui_config ?? { fields: [], conditional_rules: [], action_rules: [], buttons: [] };
                        const buttons = [...(ui.buttons ?? []), { id: "btn_" + Math.random().toString(36).slice(2, 9), label: "", action_type: "send_email" as const, action_params: { template_code: "" }, visibility_condition: null, context_binding: null }];
                        setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                      }}
                      icon={<Plus size={14} />}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Libellé, action (courriel, coût, inventaire), condition d&apos;affichage, contexte.</p>
                  {((activityForm.activity_ui_config?.buttons) ?? []).map((btn, idx) => (
                    <div key={btn.id} className="p-3 border border-border rounded bg-surface-subtle space-y-2 mb-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        <Input
                          value={btn.label}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                            const buttons = [...(ui.buttons ?? [])];
                            buttons[idx] = { ...btn, label: e.target.value };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                          }}
                          placeholder="Libellé du bouton"
                          className="flex-1 min-w-[140px] text-sm"
                        />
                        <select
                          value={btn.action_type}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                            const buttons = [...(ui.buttons ?? [])];
                            const type = e.target.value as ActivityUIButton["action_type"];
                            buttons[idx] = { ...btn, action_type: type, action_params: type === "send_email" ? { template_code: "" } : type === "add_operational_cost" ? { supplier_product_id: "" } : { supplier_product_id: "", quantity: 1 } };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                          }}
                          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                        >
                          <option value="send_email">Envoyer courriel</option>
                          <option value="add_operational_cost">Ajouter coût</option>
                          <option value="consume_inventory">Consommer inventaire</option>
                        </select>
                        <select
                          value={btn.context_binding ?? "fixed"}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                            const buttons = [...(ui.buttons ?? [])];
                            buttons[idx] = { ...btn, context_binding: (e.target.value || null) as ActivityUIButton["context_binding"] };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                          }}
                          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                        >
                          <option value="fixed">Référence fixe</option>
                          <option value="task_product">Produit de la tâche</option>
                          <option value="task_invoice_client">Client de la facture</option>
                        </select>
                        <IconButton icon={<Trash2 size={14} />} ariaLabel="Supprimer" intent="ghost" size="sm" onClick={() => {
                          const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                          const buttons = (ui.buttons ?? []).filter((_, i) => i !== idx);
                          setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                        }} />
                      </div>
                      <div className="flex gap-2 items-center">
                        <Input
                          value={btn.action_type === "send_email" ? String((btn.action_params as { template_code?: string })?.template_code ?? "") : String((btn.action_params as { supplier_product_id?: string })?.supplier_product_id ?? "")}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                            const buttons = [...(ui.buttons ?? [])];
                            const key = btn.action_type === "send_email" ? "template_code" : "supplier_product_id";
                            buttons[idx] = { ...btn, action_params: { ...btn.action_params, [key]: e.target.value } };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                          }}
                          placeholder={btn.action_type === "send_email" ? "Code template" : "ID produit fournisseur"}
                          className="flex-1 text-sm"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">Afficher si :</div>
                      <div className="flex flex-wrap gap-2 items-center">
                        <select
                          value={btn.visibility_condition?.source_field_id ?? ""}
                          onChange={(e) => {
                            const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                            const buttons = [...(ui.buttons ?? [])];
                            buttons[idx] = { ...btn, visibility_condition: e.target.value ? { source_field_id: e.target.value, operator: btn.visibility_condition?.operator ?? "equals", value: btn.visibility_condition?.value } : null };
                            setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                          }}
                          className="h-8 rounded-md border border-border bg-surface px-2 text-sm min-w-[100px]"
                        >
                          <option value="">— Toujours —</option>
                          {((activityForm.activity_ui_config?.fields) ?? []).map((f) => (
                            <option key={f.id} value={f.id}>{f.label || f.id}</option>
                          ))}
                        </select>
                        {(btn.visibility_condition?.source_field_id) && (
                          <>
                            <select
                              value={btn.visibility_condition?.operator ?? "equals"}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                                const buttons = [...(ui.buttons ?? [])];
                                buttons[idx] = { ...btn, visibility_condition: btn.visibility_condition ? { ...btn.visibility_condition, operator: e.target.value as ActivityUIButtonVisibility["operator"] } : null };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                              }}
                              className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                            >
                              <option value="equals">=</option>
                              <option value="not_equals">≠</option>
                            </select>
                            <Input
                              value={btn.visibility_condition?.value ?? ""}
                              onChange={(e) => {
                                const ui = activityForm.activity_ui_config ?? { fields: [], buttons: [] };
                                const buttons = [...(ui.buttons ?? [])];
                                buttons[idx] = { ...btn, visibility_condition: btn.visibility_condition ? { ...btn.visibility_condition, value: e.target.value } : null };
                                setActivityForm({ ...activityForm, activity_ui_config: { ...ui, buttons } });
                              }}
                              placeholder="Valeur"
                              className="w-24 text-sm"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {activityForm.activity_type === "add_operational_cost" && (
                <div className="border-t border-border pt-3 space-y-3">
                  <h4 className="text-sm font-medium text-text">Paramétrage coût opérationnel</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-text">Source du coût</label>
                    <select
                      value={activityForm.cost_activity_config?.cost_source ?? ""}
                      onChange={(e) => setActivityForm({
                        ...activityForm,
                        cost_activity_config: {
                          ...(activityForm.cost_activity_config ?? {}),
                          cost_source: e.target.value || undefined,
                        },
                      })}
                      className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                    >
                      <option value="">—</option>
                      <option value="supplier_product">Fournisseur (produit)</option>
                      <option value="internal_labor">Main d&apos;œuvre interne</option>
                      <option value="operational_fixed">Coût fixe opérationnel</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-text">Formule quantité (ex: 1, qty, qty * 2)</label>
                    <Input
                      value={activityForm.cost_activity_config?.quantity_formula ?? ""}
                      onChange={(e) => setActivityForm({
                        ...activityForm,
                        cost_activity_config: {
                          ...(activityForm.cost_activity_config ?? {}),
                          quantity_formula: e.target.value || undefined,
                        },
                      })}
                      placeholder="ex. 1"
                    />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activityForm.cost_activity_config?.is_mandatory ?? false}
                        onChange={(e) => setActivityForm({
                          ...activityForm,
                          cost_activity_config: {
                            ...(activityForm.cost_activity_config ?? {}),
                            is_mandatory: e.target.checked,
                          },
                        })}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-text">Obligatoire</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activityForm.cost_activity_config?.allow_override ?? false}
                        onChange={(e) => setActivityForm({
                          ...activityForm,
                          cost_activity_config: {
                            ...(activityForm.cost_activity_config ?? {}),
                            allow_override: e.target.checked,
                          },
                        })}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-text">Autoriser la surcharge</span>
                    </label>
                  </div>
                </div>
              )}
              {activityForm.activity_type === "assign_responsible" && (
                <div className="border-t border-border pt-3 space-y-3">
                  <h4 className="text-sm font-medium text-text">Qui peut être sélectionné comme responsable</h4>
                  <p className="text-xs text-muted-foreground">
                    Selon la configuration, le processus proposera les bons utilisateurs au moment de l&apos;assignation.
                  </p>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-text">Type d&apos;utilisateur</label>
                    <select
                      value={activityForm.assignee_activity_config?.assignee_source ?? "client_ambassador"}
                      onChange={(e) => {
                        const v = e.target.value as AssigneeActivityConfig["assignee_source"];
                        setActivityForm({
                          ...activityForm,
                          assignee_activity_config: {
                            ...(activityForm.assignee_activity_config ?? { assignee_source: "client_ambassador" }),
                            assignee_source: v,
                            ambassador_org_id: v === "specific_ambassador" ? (activityForm.assignee_activity_config?.ambassador_org_id ?? null) : undefined,
                            role_id: v === "internal_by_role" ? (activityForm.assignee_activity_config?.role_id ?? null) : undefined,
                          },
                        });
                      }}
                      className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                    >
                      <option value="client_ambassador">Associé à l&apos;ambassadeur du client</option>
                      <option value="specific_ambassador">Associé à un ambassadeur précis</option>
                      <option value="internal_by_role">Interne XLKey avec le rôle…</option>
                    </select>
                  </div>
                  {activityForm.assignee_activity_config?.assignee_source === "specific_ambassador" && (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-text">Ambassadeur</label>
                      <select
                        value={activityForm.assignee_activity_config?.ambassador_org_id ?? ""}
                        onChange={(e) => setActivityForm({
                          ...activityForm,
                          assignee_activity_config: {
                            ...(activityForm.assignee_activity_config ?? { assignee_source: "specific_ambassador" }),
                            ambassador_org_id: e.target.value || null,
                          },
                        })}
                        className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                      >
                        <option value="">— Choisir —</option>
                        {ambassadors.map((amb) => (
                          <option key={amb.id} value={amb.id}>{amb.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {activityForm.assignee_activity_config?.assignee_source === "internal_by_role" && (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-text">Rôle (XLKey interne)</label>
                      <select
                        value={activityForm.assignee_activity_config?.role_id ?? ""}
                        onChange={(e) => setActivityForm({
                          ...activityForm,
                          assignee_activity_config: {
                            ...(activityForm.assignee_activity_config ?? { assignee_source: "internal_by_role" }),
                            role_id: e.target.value || null,
                          },
                        })}
                        className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                      >
                        <option value="">— Choisir —</option>
                        {customRoles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              {activityForm.activity_type === "send_email_manual" && (
                <div className="border-t border-border pt-3 space-y-3">
                  <h4 className="text-sm font-medium text-text">Paramétrage courriel</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-text">Template de courriel</label>
                    <Select
                      label=""
                      hideLabel
                      value={emailTemplateOptions.find((o) => o.value === (activityForm.email_template_code ?? ""))?.label ?? "Aucun template"}
                      onChange={(value) => {
                        const opt = emailTemplateOptions.find((o) => o.label === value);
                        setActivityForm({ ...activityForm, email_template_code: opt?.value ?? "" });
                      }}
                      options={emailTemplateOptions.map((o) => o.label)}
                    />
                  </div>
                  {(activityForm.email_template_code ?? "").length > 0 && (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-text">Destinataires (Principal, CC, CCI)</label>
                      {(() => {
                        const recs = activityForm.email_recipients?.length ? activityForm.email_recipients : [{ recipient_type: activityForm.email_recipient_type || "client", role: "to" }];
                        const recLabel = (t: string) => EMAIL_RECIPIENT_OPTIONS.find((o) => o.value === t)?.label ?? "Client";
                        const roleOpts = [{ value: "to", label: "Principal (destinataire)" }, { value: "cc", label: "CC" }, { value: "bcc", label: "CCI" }];
                        const updateRecs = (next: Array<{ recipient_type: string; role: string }>) => setActivityForm({ ...activityForm, email_recipients: next });
                        return (
                          <div className="space-y-2">
                            {recs.map((r, i) => (
                              <div key={i} className="flex flex-wrap items-center gap-2">
                                <Select label="" hideLabel value={recLabel(r.recipient_type)} onChange={(v) => { const o = EMAIL_RECIPIENT_OPTIONS.find((x) => x.label === v); const n = [...recs]; n[i] = { ...n[i], recipient_type: o?.value ?? "client" }; updateRecs(n); }} options={EMAIL_RECIPIENT_OPTIONS.map((o) => o.label)} />
                                <Select label="" hideLabel value={roleOpts.find((o) => o.value === r.role)?.label ?? "Principal (destinataire)"} onChange={(v) => { const n = [...recs]; n[i] = { ...n[i], role: roleOpts.find((o) => o.label === v)?.value ?? "to" }; updateRecs(n); }} options={roleOpts.map((o) => o.label)} />
                                {recs.length > 1 && <IconButton icon={<X size={14} />} ariaLabel="Retirer" intent="ghost" size="sm" onClick={() => updateRecs(recs.filter((_, idx) => idx !== i))} />}
                              </div>
                            ))}
                            <Button label="Ajouter un destinataire" intent="ghost" size="sm" onClick={() => updateRecs([...recs, { recipient_type: "client", role: "to" }])} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  label="Enregistrer"
                  intent="primary"
                  size="sm"
                  onClick={handleSaveActivity}
                />
                <Button
                  label="Annuler"
                  intent="default"
                  size="sm"
                  onClick={() => {
                    setEditingActivityIndex(null);
                    setIsAddingActivity(false);
                    setActivityForm({ title: "", description: "", is_required: true, activity_type: "other", email_template_code: "", email_recipient_type: "client", email_recipients: [], cost_activity_config: null, assignee_activity_config: null, activity_ui_config: null });
                  }}
                />
              </div>
            </div>
          )}
        </div>
        </div>
            )}

            {activeSection === "triggers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-text">Triggers</h3>
            <Button
              label="Ajouter un trigger"
              intent="default"
              size="sm"
              onClick={handleAddTrigger}
            />
          </div>
          {editingTriggerIndex === null && !isAddingTrigger ? (
            <div className="space-y-2">
              {localTriggers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucun trigger défini</p>
              ) : (
                localTriggers.map((trigger, index) => (
                  <div key={index} className="p-3 border border-border rounded-lg bg-surface flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{trigger.title || TRIGGER_ACTION_OPTIONS.find((opt) => opt.value === trigger.action_type)?.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {TRIGGER_EVENT_OPTIONS.find((opt) => opt.value === trigger.event_type)?.label}
                        </span>
                      </div>
                      {trigger.description && (
                        <p className="text-xs text-muted-foreground mt-1">{trigger.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <IconButton
                        icon={<Pencil size={14} />}
                        ariaLabel="Modifier"
                        intent="ghost"
                        size="sm"
                      onClick={() => {
                        setEditingTriggerIndex(index);
                        setIsAddingTrigger(false);
                        setTriggerForm({
                          title: trigger.title,
                          description: trigger.description,
                          event_type: trigger.event_type,
                          action_type: trigger.action_type,
                          action_payload: trigger.action_payload,
                        });
                      }}
                      />
                      <IconButton
                        icon={<Trash2 size={14} />}
                        ariaLabel="Supprimer"
                        intent="ghost"
                        size="sm"
                        onClick={() => handleDeleteTrigger(index)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="p-4 border border-border rounded-lg bg-surface-muted space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Nom / Titre du trigger *</label>
                <Input
                  label=""
                  hideLabel
                  value={triggerForm.title}
                  onChange={(e) => setTriggerForm({ ...triggerForm, title: e.target.value })}
                  placeholder="Ex: Alerte étape en retard"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Description (facultatif)</label>
                <textarea
                  className="w-full min-h-[60px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text resize-y"
                  value={triggerForm.description}
                  onChange={(e) => setTriggerForm({ ...triggerForm, description: e.target.value })}
                  placeholder="Description du trigger..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-text">Événement déclencheur</label>
                  <Select
                    label=""
                    hideLabel={true}
                    value={TRIGGER_EVENT_OPTIONS.find((opt) => opt.value === triggerForm.event_type)?.label || ""}
                    onChange={(value) => {
                      const option = TRIGGER_EVENT_OPTIONS.find((opt) => opt.label === value);
                      if (option) {
                        setTriggerForm({ ...triggerForm, event_type: option.value as TriggerEventType });
                      }
                    }}
                    options={TRIGGER_EVENT_OPTIONS.map((opt) => opt.label)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-text">Action</label>
                  <Select
                    label=""
                    hideLabel={true}
                    value={TRIGGER_ACTION_OPTIONS.find((opt) => opt.value === triggerForm.action_type)?.label || ""}
                    onChange={(value) => {
                      const option = TRIGGER_ACTION_OPTIONS.find((opt) => opt.label === value);
                      if (option) {
                        setTriggerForm({ ...triggerForm, action_type: option.value as TriggerActionType, action_payload: {} });
                      }
                    }}
                    options={TRIGGER_ACTION_OPTIONS.map((opt) => opt.label)}
                  />
                </div>
              </div>
              <TriggerActionParams
                actionType={triggerForm.action_type}
                payload={triggerForm.action_payload}
                onChange={(action_payload) => setTriggerForm({ ...triggerForm, action_payload })}
              />
              <div className="flex gap-2">
                <Button
                  label="Enregistrer"
                  intent="primary"
                  size="sm"
                  onClick={handleSaveTrigger}
                />
                <Button
                  label="Annuler"
                  intent="default"
                  size="sm"
                  onClick={() => {
                    setEditingTriggerIndex(null);
                    setIsAddingTrigger(false);
                    setTriggerForm({ title: "", description: "", event_type: "step_started", action_type: "create_alert", action_payload: {} });
                  }}
                />
              </div>
            </div>
          )}
        </div>
            )}

            {activeSection === "entry" && (
              <div className="space-y-4">
                <label className="block text-sm font-medium mb-2 text-text">Conditions d'entrée</label>
                <p className="text-xs text-muted mb-2">Définissent quand cette étape peut être démarrée.</p>
                <StepConditionsBuilder
                  conditions={form.entry_conditions as StepConditions | null}
                  onChange={(conditions) => setForm({ ...form, entry_conditions: conditions })}
                  availableSteps={[]}
                  availableActivities={[]}
                />
              </div>
            )}

            {activeSection === "exit" && (
              <div className="space-y-4">
                <label className="block text-sm font-medium mb-2 text-text">Conditions de sortie</label>
                <p className="text-xs text-muted mb-2">Définissent quand cette étape est considérée comme terminée (en plus de la politique de complétion).</p>
                <StepConditionsBuilder
                  conditions={form.exit_conditions as StepConditions | null}
                  onChange={(conditions) => setForm({ ...form, exit_conditions: conditions })}
                  availableSteps={[]}
                  availableActivities={[]}
                />
              </div>
            )}

            {activeSection === "completion" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <label className="block text-sm font-medium text-text shrink-0">Politique de complétion *</label>
                    <span
                      className="group relative shrink-0"
                      title="Définit comment l'étape est marquée complète : toutes les activités requises, validation manuelle par l'assigné, confirmation externe, ou critère temporel."
                    >
                      <HelpCircle size={14} className="text-muted cursor-help" />
                      <span className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block w-72 max-w-[calc(100vw-2rem)] p-3 text-xs leading-relaxed rounded-md bg-surface-muted border border-border text-text shadow-lg whitespace-normal">
                        Définit comment l'étape est marquée complète : toutes les activités requises, validation manuelle par l'assigné, confirmation externe, ou critère temporel.
                      </span>
                    </span>
                  </div>
                  <Select
                    label=""
                    hideLabel={true}
                    value={
                      form.completion_policy === "all_required"
                        ? "Toutes les activités requises"
                        : form.completion_policy === "manual_by_assignee"
                          ? "Manuelle par l'assigné"
                          : form.completion_policy === "external_confirmation"
                            ? "Confirmation externe"
                            : form.completion_policy === "time_based"
                              ? "Basée sur le temps"
                              : ""
                    }
                    onChange={(value) => {
                      const policyMap: Record<string, CompletionPolicy> = {
                        "Toutes les activités requises": "all_required",
                        "Manuelle par l'assigné": "manual_by_assignee",
                        "Confirmation externe": "external_confirmation",
                        "Basée sur le temps": "time_based",
                      };
                      setForm({ ...form, completion_policy: policyMap[value] || "all_required" });
                    }}
                    options={[
                      "Toutes les activités requises",
                      "Manuelle par l'assigné",
                      "Confirmation externe",
                      "Basée sur le temps",
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            label="Annuler"
            intent="default"
            size="sm"
            onClick={() => onOpenChange(false)}
          />
        </div>
      </div>
    </Modal>
  );
}
